import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";
import cx from "classnames";
import { Image } from "react-bootstrap";
import dateformat from 'dateformat';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { DealRoomContext } from "../context";
import { DealRoomAsset, StakeHolderTypeEnum } from "src/types/dealRoom";
import { SmartCardTypes, transformSmartCards } from "src/services/dealRoom.helper";

import styles from "./styles.module.scss";

const DefaultAssetIcon = '/images/defaultIcons/default_asset.jpg';
const DefaultUserIcon = '/images/default_user_profile_background.png';

interface SmartCardsListProps {
  handleMapDealClick: () => void;
  showDealDetails: () => void;
}

const SmartCardsList: React.FC<SmartCardsListProps> = ({ handleMapDealClick, showDealDetails }) => {
  const navigate = useNavigate();

  const [cards, setCards] = useState<any[]>([]);

  const { id, dealRoom, menuState } = useContext(DealRoomContext);
  const isEditing = menuState.primaryAction === 'edit';
 

  const smartCardsData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom["${id}"].smartCards`, []);
  });
  const { loading, items: smartCards } = smartCardsData;

  const isDealMapped = _.get(dealRoom, "opportunityMapped", false);
  const dealRoomOwnerObj = _.get(dealRoom, "createdBy", {});
  const dealRoomOwner = `${_.get(dealRoomOwnerObj, "firstName", "")} ${_.get(dealRoomOwnerObj, "lastName", "")}`;

  const accountItem = useSelector((state: any) => {
    return _.get(state, "accounts.item")
  });
  const isDealInsightsIntegrated = _.get(accountItem, "dealInsightsEnabled", false);

  useEffect(() => {
    const cards = transformSmartCards(smartCards, isDealMapped, isDealInsightsIntegrated, dealRoomOwner, isEditing);
    setCards(cards);
  }, [smartCards, isEditing])


  const onCtaClick = (cardType: string) => {
    switch (cardType) {
      case SmartCardTypes.DEAL_PROBABILITY:
        if (!isDealInsightsIntegrated) {
          return;
        }

        if (isDealMapped) {
          showDealDetails();
        } else {
          handleMapDealClick();
        }
        break;

      case SmartCardTypes.ACTIVE_PEOPLE:
        navigate(`/deal-room/${id}/engage`);
        break;

      case SmartCardTypes.MAP_DEAL:
        handleMapDealClick();
        break;

      case SmartCardTypes.PARTICIPANT_ENGAGEMENT:
        navigate(`/deal-room/${id}/activity`);
        break;

      case SmartCardTypes.MESSAGE_LIST:
        navigate(`/deal-room/${id}/engage`);
        break;

      case SmartCardTypes.RECENT_UPLOADS:
        navigate(`/deal-room/${id}/assets/external`);
        break;

      case SmartCardTypes.NEW_PARTICIPANT:
        navigate(`/deal-room/${id}/people/external`);
        break;

      default:
        break;
    }
  }

  if (loading) {
    return (
      <div className={cx(styles.container, styles.loading, styles.placeholder)}>
        <div className={cx(styles.card, styles.block)}></div>
        <div className={cx(styles.card, styles.block)}></div>
        <div className={cx(styles.card, styles.doubleWidth, styles.block)}></div>
      </div>
    )
  }

  const shouldHideDealInsightsCard = () => {

    if(_.get(dealRoom, "canEdit", false)){
      return false;
    }

    if(isDealMapped && isDealInsightsIntegrated){
      return false;
    }

    return true;
  }
  const dealRoomEngagement = _.find(smartCards, { type: "DEALROOM_ENGAGEMENT" });

  return (
    <div className={styles.container}>
      {_.map(cards, (card, index) => {
        const cardType = _.get(card, 'type');
        const conditions = {
          [SmartCardTypes.RECENT_UPLOADS]: _.size(_.get(card, 'assets', [])) <= 0,
          [SmartCardTypes.PARTICIPANT_ENGAGEMENT]: _.get(card, 'timeSpent', 0) <= 5,
          [SmartCardTypes.NEW_PARTICIPANT]: _.size(_.get(card, 'externalPeople', [])) <= 0,
          [SmartCardTypes.ACTIVE_PEOPLE] : !_.get(dealRoomEngagement,"hasExternalRecipients", false),
          [SmartCardTypes.MESSAGE_LIST] : !_.get(dealRoomEngagement,"hasExternalRecipients", false),
          [SmartCardTypes.DEALROOM_ENGAGEMENT]: true,
          [SmartCardTypes.MAP_DEAL]: !_.get(dealRoom, "canEdit", false),
          [SmartCardTypes.DEAL_PROBABILITY]:  shouldHideDealInsightsCard()
        };

        if (conditions[cardType]) {
          return <></>;
        }

        return (
          <div
            className={cx(styles.card, {
              [styles.doubleHeight]: _.get(card, "height", 1) === 2,
              [styles.doubleWidth]: _.get(card, "width", 1) === 2,
              [styles.maxHeight]: isEditing,
            })}
            key={`card-${index}`}
          >
            {/* 
              TITLE
              - if cardType is ACTIVE_PEOPLE, show the user images
            */}
            {cardType && (
              cardType === SmartCardTypes.ACTIVE_PEOPLE && _.get(card, 'totalActiveUsers', 0) > 0 ? (
                <div className={styles.activePeopleContainer}>
                  {_.map(_.get(card, 'chatUsers', []), (user, index) => {
                    return (
                      <div className={cx(styles.user, styles[`user-${index + 1}`])} key={`user-${index + 1}`}>
                        <img src={_.get(user, 'icon') ?? DefaultUserIcon} alt="user" className={styles.userImage} />
                      </div>
                    )
                  })}
                </div>
              ) : (
                _.get(card, 'title') && (
                  <div>
                    <p className={styles.header}>{_.get(card, 'title')}</p>
                    {cardType === SmartCardTypes.DEAL_PROBABILITY && isDealMapped && isDealInsightsIntegrated && <p className={styles.secondHeading}>Win Probability</p>}
                  </div>
                )
              )
            )}

            {/* SUBTEXT */}
            {
              _.get(card, 'subtext') && <p className={styles.subtext}>{_.get(card, 'subtext')}</p>
            }


            {/* RECENTLY UPLOADED ASSETS */}
            {cardType === SmartCardTypes.RECENT_UPLOADS && (
              <AssetsRenderer assets={_.get(card, 'assets', [])} />
            )}

            {/* NEWLY ADDED PARTICIPANTS */}
            {cardType === SmartCardTypes.NEW_PARTICIPANT && (
              <UsersRenderer
                externalPeople={_.get(card, 'externalPeople', [])}
                totalPeopleCount={_.get(card, 'totalPeople', 0)}
              />
            )}

            {/* CTA */}
            {_.get(card, 'ctaText') && (
              <div
                className={styles.cta}
                onClick={() => onCtaClick(cardType)}
              >
                <div className={styles.iconContainer}><i className={cx("paperflite-arrow-up", styles.arrowIcon)}></i></div>
                <p id={!isDealInsightsIntegrated && cardType === SmartCardTypes.DEAL_PROBABILITY ? 'intercom_launcher' : ''}>{_.get(card, 'ctaText', "more details")}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  )
};

const AssetsRenderer = ({ assets }) => {
  const navigate = useNavigate();
  const { dealRoom } = useContext(DealRoomContext);

  const handleAssetClick = (asset: DealRoomAsset) => {
    let source = `deal-room/${dealRoom.id}/overview`;
    const url = `/assets/${_.get(asset, 'id')}?source=${source}`;
    navigate(url);
  }

  if (!(_.size(assets) > 0)) {
    return <></>
  }

  return (
    <div className={styles.assetsContainer}>
      {_.map(assets, asset => {
        return (
          <div onClick={() => handleAssetClick(asset)} className={styles.asset} key={_.get(asset, 'id')}>
            <div className={styles.assetThumbnail}>
              <Image
                
                className={styles.assetImage}
                src={_.get(asset, 'icon.thumbnail') ?? DefaultAssetIcon}
              />
            </div>
            <div className={styles.assetDetail}>
              <p className={styles.assetTitle}>
                <div style={{ maxWidth: '200px' }} className={styles.truncatedText}>{_.get(asset, 'name', '')}</div>
              </p>
              <p className={styles.assetUploadedDate}>
                <div className={styles.truncatedText}>
                  {`Uploaded on ${dateformat(_.get(asset, 'createdDate'), 'dd mmm yyyy')}`}
                </div>
              </p>
            </div>
          </div>
        )
      })
      }
    </div>
  )
}

const UsersRenderer = ({ externalPeople, totalPeopleCount }) => {
  if (!(totalPeopleCount > 0 && _.size(externalPeople) > 0)) {
    return <></>
  }

  return (
    <div className={styles.parentContainer}>
      <div className={cx(styles.usersContainer, {
        [styles.userContainerMask]: totalPeopleCount > 5,
      })}>
        {
          _.map(externalPeople, user => {
            return (
              <div className={styles.user} key={_.get(user, 'id')}>
                <div className={styles.userProfilePicture}>
                  <Image
                    onClick={() => { }}
                    className={styles.image}
                    src={_.get(user, 'icon.thumbnail') ?? DefaultUserIcon}
                  />
                </div>
                <p className={styles.userName}>
                  <div style={{ maxWidth: '800px' }} className={styles.truncatedText}>
                    {`${_.get(user, 'firstName') ? _.get(user, 'firstName', '') : ''} ${_.get(user, 'lastName') ? _.get(user, 'lastName', '') : ''}`}
                  </div>
                </p>
              </div>
            )
          })
        }
      </div>
    </div>
  )
}

export default SmartCardsList;
